import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PageType } from "@reconvert/reconvert-utils";
import type { RootState } from "../store";

export type AIChatCurrentView = "FLOW_BUILDER";
export type AIChatSlotStatus = "ENABLED" | "DISABLED";

/**
 * Mirrors the union of fields that are sent to the AI Chatbot via
 * `UPDATE_AI_CHAT_CONTEXT` postMessages. Each field maps 1:1 to an
 * `AIChatbotPostMessageEventType` so the sync hook can emit one targeted
 * postMessage per change.
 */
export interface AIChatContextState {
  currentView: AIChatCurrentView;
  page?: PageType;
  conditionId: string;
  variantId?: string;
  slotId?: string;
  slotStatus?: AIChatSlotStatus;
  hasUnsavedChanges: boolean;
}

const initialState: AIChatContextState = {
  currentView: "FLOW_BUILDER",
  page: undefined,
  conditionId: "",
  variantId: undefined,
  slotId: undefined,
  slotStatus: undefined,
  hasUnsavedChanges: false,
};

const aiChatContextSlice = createSlice({
  name: "aiChatContext",
  initialState,
  reducers: {
    setAiChatCurrentView: (state, action: PayloadAction<AIChatCurrentView>) => {
      state.currentView = action.payload;
    },
    setAiChatPage: (state, action: PayloadAction<PageType | undefined>) => {
      state.page = action.payload;
    },
    setAiChatConditionId: (state, action: PayloadAction<string>) => {
      state.conditionId = action.payload;
    },
    setAiChatVariantId: (state, action: PayloadAction<string | undefined>) => {
      state.variantId = action.payload;
    },
    setAiChatSlotId: (state, action: PayloadAction<string | undefined>) => {
      state.slotId = action.payload;
    },
    setAiChatSlotStatus: (state, action: PayloadAction<AIChatSlotStatus | undefined>) => {
      state.slotStatus = action.payload;
    },
    setAiChatHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
  },
});

export const {
  setAiChatCurrentView,
  setAiChatPage,
  setAiChatConditionId,
  setAiChatVariantId,
  setAiChatSlotId,
  setAiChatSlotStatus,
  setAiChatHasUnsavedChanges,
} = aiChatContextSlice.actions;

export const selectAiChatContext = (state: RootState) => state.aiChatContext;

export default aiChatContextSlice.reducer;
